import { Component, h, Prop, Event, EventEmitter, Method, State, Listen, Element } from '@stencil/core';
import { getConstant } from '../../../../utils/constants-helpers';

// Interface for form data object
interface FormDataType {
  [key: string]: unknown;
}

// Interface for custom form elements
interface CustomFormElement extends HTMLElement {
  name: string;
  value?: string | boolean;
  checked?: boolean;
  disabled?: boolean;
  required?: boolean;
}

// Interface for validation errors with type
interface ValidationErrors {
  [key: string]: { message: string; type: 'required' | 'invalid' };
  submit?: { message: string; type: 'required' | 'invalid' };
}

/**
 * Form validation container component that handles submission, validation, and error display.
 * Wraps form fields in a form element with configurable action and provides success/error state management.
 *
 * @constant TEXT_FORM_MESSAGE_FIELD_REQUIRED - Message shown when a required field is empty
 * @constant TEXT_FORM_MESSAGE_FIELD_IS_INVALID - Message shown when a field contains invalid data
 * @constant TEXT_FORM_SUBMIT_BUTTON - Text displayed on the submit button
 * @constant TEXT_FORM_RESET_BUTTON - Text displayed on the reset button
 * @constant TEXT_FORM_SUCCESS_MESSAGE - Message shown on successful form submission
 * @example
 *   <form-handler-type-1 action="sign-up"></form-handler-type-1>
 */

@Component({
  tag: 'form-handler-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class FormHandlerType1 {
  @Element() hostEl!: HTMLElement;
  private formRef?: HTMLFormElement;

  /**
   * Component constants object containing all required text constants
   */
  @Prop() constants?: Record<string, string>;

  @State() isSubmitting: boolean = false;
  @State() errors: ValidationErrors = {};
  @State() focusedField: string | null = null;
  @State() wasSubmitted: boolean = false;

  /**
   * URL for form submission. If not specified, submitHandler will be used
   */
  @Prop() action?: string;

  /**
   * When true, field names are submitted as-is instead of being prefixed with the
   * auto-generated form name (e.g. "firstname" instead of "div-firstname"). Use this
   * when the submission endpoint expects exact field names.
   */
  @Prop() disableFieldPrefix?: boolean = false;

  /**
   * HTTP method for form submission
   */
  @Prop() method?: 'get' | 'post' = 'post';

  /**
   * Request body encoding for POST submissions.
   * - `multipart` (default): sends a `FormData` body → `Content-Type: multipart/form-data`.
   * - `urlencoded`: sends a `URLSearchParams` body → `Content-Type: application/x-www-form-urlencoded`.
   * Use `urlencoded` for endpoints that reject multipart (e.g. the signNow lead capture / herald-api).
   * Only safe for text fields — do not use with file inputs.
   */
  @Prop() encoding?: 'multipart' | 'urlencoded' = 'multipart';

  /**
   * Callback for custom form submission handling
   */
  @Prop() submitHandler?: (data: FormDataType) => Promise<void>;

  /**
   * Event emitted on successful form submission
   */
  @Event() formSubmitSuccess: EventEmitter<FormDataType>;

  /**
   * Event emitted on form submission error
   */
  @Event() formSubmitError: EventEmitter<Error>;

  /**
   * Event emitted on submission state change
   */
  @Event() formSubmitting: EventEmitter<boolean>;

  private formName: string = '';

  /**
   * Get or generate a unique form name based on the parent component and uniqueness on the page
   */
  private getUniqueFormName(): string {
    // Get the parent component's tag name
    const parentTag = this.hostEl.parentElement?.tagName.toLowerCase() || 'form';
    // Collect all forms with data-form-name starting with parentTag
    const forms = Array.from(document.querySelectorAll('form[data-form-name]'));
    const sameNames = forms.map(f => f.getAttribute('data-form-name')).filter(name => name && name.startsWith(parentTag));
    if (!sameNames.includes(parentTag)) {
      return parentTag;
    }
    let i = 2;
    while (sameNames.includes(`${parentTag}-${i}`)) {
      i++;
    }
    return `${parentTag}-${i}`;
  }

  /**
   * Get all custom form elements
   */
  private getCustomFormElements(): CustomFormElement[] {
    if (!this.formRef) return [];

    return Array.from(this.formRef.querySelectorAll('[name]')).filter((element): element is CustomFormElement => {
      return element.tagName.toLowerCase().startsWith('me-');
    });
  }

  /**
   * Convert FormData to object, including data from custom elements
   */
  private formDataToObject(formData: FormData): FormDataType {
    const object: FormDataType = {};

    // Collect data from standard form elements
    formData.forEach((value, key) => {
      if (value instanceof File) {
        object[key] = value;
      } else {
        object[key] = value.toString();
      }
    });

    // Collect data from custom elements
    const customElements = this.getCustomFormElements();
    customElements.forEach(element => {
      if (element.name) {
        // For checkbox and toggle use checked property
        if (element.tagName.toLowerCase().includes('checkbox') || element.tagName.toLowerCase().includes('toggle')) {
          object[element.name] = element.checked;
        }
        // For other elements use value property
        else if (element.value !== undefined) {
          object[element.name] = element.value;
        }
      }
    });

    return object;
  }

  /**
   * Get a unique field name (for standard and custom elements)
   */
  private getFieldResolvedName(el: any): string {
    if (typeof el.getResolvedName === 'function') {
      return el.getResolvedName();
    }
    if (this.disableFieldPrefix) {
      return el.name || '';
    }
    if (el.name) {
      // Do not add prefix if it already exists
      if (el.name.startsWith(this.formName + '-')) {
        return el.name;
      }
      return `${this.formName}-${el.name}`;
    }
    return '';
  }

  /**
   * Find a standard element by unique name
   */
  private findStandardFieldByName(name: string) {
    if (!this.formRef) return null;
    return Array.from(this.formRef.querySelectorAll('input, select, textarea')).find(el => {
      return this.getFieldResolvedName(el) === name;
    });
  }

  /**
   * Validate form and collect validation errors
   * Now returns error type: 'required' or 'invalid' for each field
   * Supports custom messages for required, minLength, maxLength, email
   */
  private validateForm(): { isValid: boolean; errors: ValidationErrors } {
    if (!this.formRef) return { isValid: false, errors: {} };

    const errors: ValidationErrors = {};
    let isValid = true;

    // Validate standard form elements (input, select, textarea)
    const standardElements = Array.from(this.formRef.querySelectorAll('input, select, textarea')).filter(el => !el.tagName.toLowerCase().startsWith('me-')) as (
      | HTMLInputElement
      | HTMLSelectElement
      | HTMLTextAreaElement
    )[];

    standardElements.forEach(element => {
      if (!element.checkValidity()) {
        isValid = false;
        if (element.name) {
          let type: 'required' | 'invalid' = 'invalid';
          let message = element.validationMessage || getConstant(this.constants, 'TEXT_FORM_MESSAGE_FIELD_IS_INVALID', 'This field is invalid');
          if (element.validity.valueMissing) {
            type = 'required';
            message = getConstant(this.constants, 'TEXT_FORM_MESSAGE_FIELD_REQUIRED', 'This field is required');
          } else if (element.validity.typeMismatch && element.type === 'email') {
            type = 'invalid';
            message = getConstant(this.constants, 'TEXT_FORM_MESSAGE_EMAIL_INVALID', 'This value seems to be invalid');
          } else if ((element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) && element.validity.tooShort) {
            type = 'invalid';
            message = `Minimum length is ${element.minLength}`;
          } else if ((element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) && element.validity.tooLong) {
            type = 'invalid';
            message = `Maximum length is ${element.maxLength}`;
          }
          if (
            type === 'invalid' &&
            element.type === 'email' &&
            (!message || message === getConstant(this.constants, 'TEXT_FORM_MESSAGE_FIELD_IS_INVALID', 'This field is invalid'))
          ) {
            message = getConstant(this.constants, 'TEXT_FORM_MESSAGE_EMAIL_INVALID', 'This value seems to be invalid');
          }
          // Always use getFieldResolvedName
          const resolvedName = this.getFieldResolvedName(element);
          errors[resolvedName] = {
            message,
            type,
          };
        }
      }
    });

    // Validate custom elements (me-input, me-textarea, etc.)
    const customElements = this.getCustomFormElements();
    customElements.forEach(element => {
      // Explicit handling for me-checkbox
      if (element.tagName.toLowerCase() === 'me-checkbox' && element.required) {
        const input = element.querySelector('input[type="checkbox"]') as HTMLInputElement | null;
        const checked = input ? input.checked : false;
        if (!checked) {
          isValid = false;
          const resolvedName = input?.name;
          errors[resolvedName] = {
            message: (element as any).requiredMessage || getConstant(this.constants, 'TEXT_FORM_MESSAGE_FIELD_REQUIRED', 'This field is required'),
            type: 'required',
          };
        }
        return;
      }
      if (element.required) {
        let elementValid = true;
        let errorType: 'required' | 'invalid' = 'required';
        let customMsg: string | undefined;
        const value = element.value;
        if (value === undefined || value === '') {
          elementValid = false;
          errorType = 'required';
          if ('requiredMessage' in element && typeof element.requiredMessage === 'string') {
            customMsg = element.requiredMessage;
          } else {
            customMsg = element.getAttribute('required-message');
          }
        } else if (typeof value === 'string') {
          if ('minLength' in element && typeof element.minLength === 'number' && element.minLength > 0 && value.length < element.minLength) {
            elementValid = false;
            errorType = 'invalid';
            if ('minLengthMessage' in element && typeof element.minLengthMessage === 'string') {
              customMsg = element.minLengthMessage;
            } else {
              customMsg = element.getAttribute('minlength-message') || `Minimum length is ${element.minLength}`;
            }
          } else if ('maxLength' in element && typeof element.maxLength === 'number' && element.maxLength > 0 && value.length > element.maxLength) {
            elementValid = false;
            errorType = 'invalid';
            if ('maxLengthMessage' in element && typeof element.maxLengthMessage === 'string') {
              customMsg = element.maxLengthMessage;
            } else {
              customMsg = element.getAttribute('maxlength-message') || `Maximum length is ${element.maxLength}`;
            }
          } else if ('type' in element && element.type === 'email' && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) {
            elementValid = false;
            errorType = 'invalid';
            if ('emailMessage' in element && typeof element.emailMessage === 'string') {
              customMsg = element.emailMessage;
            } else if ('invalidMessage' in element && typeof element.invalidMessage === 'string') {
              customMsg = element.invalidMessage;
            } else {
              customMsg = element.getAttribute('email-message') || element.getAttribute('invalid-message');
            }
          }
        }
        if (!elementValid) {
          isValid = false;
          const defaultMsg =
            errorType === 'required'
              ? getConstant(this.constants, 'TEXT_FORM_MESSAGE_FIELD_REQUIRED', 'This field is required')
              : 'type' in element && element.type === 'email'
                ? getConstant(this.constants, 'TEXT_FORM_MESSAGE_EMAIL_INVALID', 'This value seems to be invalid')
                : getConstant(this.constants, 'TEXT_FORM_MESSAGE_FIELD_IS_INVALID', 'This field is invalid');
          // Always use getFieldResolvedName
          const resolvedName = this.getFieldResolvedName(element);
          errors[resolvedName] = {
            message: customMsg || defaultMsg,
            type: errorType,
          };
        }
      }
    });

    return { isValid, errors };
  }

  /**
   * Form submission handler
   */
  private handleSubmit = async (event: Event) => {
    event.preventDefault();
    this.isSubmitting = true;
    this.wasSubmitted = true;

    const { isValid, errors } = this.validateForm();
    this.errors = errors;
    this.isSubmitting = false;
    this.updateFieldErrors();

    if (!isValid) {
      // Do not submit if form is invalid
      return;
    }

    if (!this.formRef) return;

    const formData = new FormData(this.formRef);
    const data = this.formDataToObject(formData);

    try {
      this.formSubmitting.emit(true);

      if (this.submitHandler) {
        await this.submitHandler(data);
      } else if (this.action) {
        // Body encoding: passing a raw FormData object forces
        // `Content-Type: multipart/form-data; boundary=…`, while passing URLSearchParams
        // sets `application/x-www-form-urlencoded`. Some endpoints (e.g. the signNow lead
        // capture / herald-api) require urlencoded, so allow opting in per form.
        const body =
          this.method.toLowerCase() === 'get'
            ? null
            : this.encoding === 'urlencoded'
              ? new URLSearchParams(formData as unknown as Record<string, string>)
              : formData;

        const response = await fetch(this.action, {
          method: this.method,
          body,
        });

        if (!response.ok) {
          throw new Error('Form submission failed');
        }
      } else {
        console.warn('Neither action nor submitHandler specified');
        return;
      }

      this.formSubmitSuccess.emit(data);
      this.resetForm();
    } catch (error) {
      this.formSubmitError.emit(error as Error);
      this.errors = { submit: { message: (error as Error).message, type: 'invalid' } };
    } finally {
      this.formSubmitting.emit(false);
    }
  };

  /**
   * Reset form to initial state
   */
  @Method()
  async resetForm() {
    if (this.formRef) {
      // Reset standard form elements
      this.formRef.reset();

      // Reset custom elements
      const customElements = this.getCustomFormElements();
      customElements.forEach(element => {
        // For checkbox and toggle
        if (element.tagName.toLowerCase().includes('checkbox') || element.tagName.toLowerCase().includes('toggle')) {
          element.checked = false;
          element.dispatchEvent(new CustomEvent('valueChange', { detail: false }));
        }
        // For other elements
        else {
          element.value = '';
          element.dispatchEvent(new CustomEvent('valueChange', { detail: '' }));
        }
      });

      this.errors = {};
    }
    this.wasSubmitted = false;
  }

  /**
   * Programmatically submit the form
   */
  @Method()
  async submit() {
    if (this.formRef) {
      const submitEvent = document.createEvent('HTMLEvents');
      submitEvent.initEvent('submit', true, true);
      this.formRef.dispatchEvent(submitEvent);
    }
  }

  /**
   * Handle focus events on form elements
   */
  @Listen('focusin')
  handleFocus(event: FocusEvent) {
    const target = event.target as HTMLElement;
    const name = target && target.getAttribute('name');
    if (name) {
      // Only clear error if field was not already focused and form was not submitted
      if (!this.wasSubmitted && this.focusedField !== name && this.errors[name]) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [name]: _, ...restErrors } = this.errors;
        this.errors = restErrors;
      }
      this.focusedField = name;
    }
  }

  /**
   * Handle blur events on form elements
   */
  @Listen('focusout')
  handleBlur() {
    this.focusedField = null;
  }

  /**
   * Get all possible names for a custom field (name, resolvedName, getAttribute('name'))
   * Now supports resolvedName = `${formId}-${name}`
   */
  private getAllNamesFromCustomField(el: any): string[] {
    const names = [];
    if (typeof el.name === 'string') names.push(el.name);
    if (typeof el.getResolvedName === 'function') names.push(el.getResolvedName());
    const attrName = el.getAttribute && el.getAttribute('name');
    if (typeof attrName === 'string') names.push(attrName);
    // Add the name of the nested input/textarea/select
    const input = el.querySelector && el.querySelector('input, textarea, select');
    if (input && typeof input.name === 'string') names.push(input.name);
    return Array.from(new Set(names));
  }

  /**
   * Find a custom field by name among supported custom elements
   * Now supports resolvedName = `${formId}-${name}`
   */
  private findCustomFieldByName(name: string) {
    const allInputs = this.formRef.querySelectorAll('me-input, me-select, me-textarea, me-checkbox, me-radio, me-toggle');
    return Array.from(allInputs).find((el: any) => {
      const names = this.getAllNamesFromCustomField(el);
      return names.includes(name);
    });
  }

  /**
   * Find a custom component (me-checkbox, me-radio, me-select) by the name of the internal input/select
   */
  private findCustomComponentByNativeName(tag: string, name: string): HTMLElement | null {
    // Search for the internal input/select by name inside the custom component
    const native = this.formRef.querySelector(`${tag} input[name="${name}"], ${tag} select[name="${name}"]`);
    return native ? (native.closest(tag) as HTMLElement) : null;
  }

  /**
   * Update error state and error message for each field component (me-input, me-select, etc.)
   * Now also sets form-id attribute for custom elements
   */
  private updateFieldErrors() {
    if (!this.formRef) return;
    // Reset all errors
    const allInputs = this.formRef.querySelectorAll('me-input, me-select, me-textarea, me-checkbox, me-radio, me-toggle');
    allInputs.forEach((el: any) => {
      (el as any).error = '';
      (el as any).errorMessage = '';
      (el as any).errorType = '';
      el.removeAttribute('error');
      el.removeAttribute('error-message');
      el.removeAttribute('error-type');
      if (el.parentElement && el.parentElement.classList.contains('has-error')) {
        el.parentElement.classList.remove('has-error');
      }
    });
    // Set errors for fields with validation errors
    Object.entries(this.errors).forEach(([field, errorObj]) => {
      if (field === 'submit') return;
      // For checkbox, radio, select, search by internal name
      const tags = ['me-checkbox', 'me-radio', 'me-select'];
      let customComponent = null;
      for (const tag of tags) {
        customComponent = this.findCustomComponentByNativeName(tag, field);
        if (customComponent) break;
      }
      if (customComponent) {
        customComponent.error = 'true';
        customComponent.errorMessage = errorObj.message;
        customComponent.errorType = errorObj.type;
        customComponent.setAttribute('error', 'true');
        customComponent.setAttribute('error-message', errorObj.message);
        customComponent.setAttribute('error-type', errorObj.type);
        if (customComponent.parentElement) {
          customComponent.parentElement.classList.add('has-error');
        }
        return;
      }
      // For other components (me-input, me-textarea) use the old logic
      const input = this.findCustomFieldByName(field);
      if (input) {
        (input as any).error = 'true';
        (input as any).errorMessage = errorObj.message;
        (input as any).errorType = errorObj.type;
        input.setAttribute('error', 'true');
        input.setAttribute('error-message', errorObj.message);
        input.setAttribute('error-type', errorObj.type);
        if (input.parentElement) {
          input.parentElement.classList.add('has-error');
        }
      }
    });
  }

  @Listen('input')
  @Listen('change')
  handleInputChange(event: Event) {
    const target = event.target as HTMLElement;
    let name = target && target.getAttribute('name');
    if (!name) {
      const customParent = target.closest('me-input, me-textarea, me-select, me-checkbox, me-radio, me-toggle');
      if (customParent) {
        name = customParent.getAttribute('name');
      }
    }
    if (name) {
      this.clearErrorForField(name);
    }
  }

  @Listen('valueChange')
  handleCustomValueChange(event: CustomEvent) {
    const target = event.target as HTMLElement;
    let name = target && target.getAttribute('name');
    if (!name) {
      const customParent = target.closest('me-input, me-textarea, me-select, me-checkbox, me-radio, me-toggle');
      if (customParent) {
        name = customParent.getAttribute('name');
      }
    }
    if (name) {
      this.clearErrorForField(name);
    }
  }

  /**
   * Clear error for a specific field by name
   */
  private clearErrorForField(name: string) {
    // Search for the element by unique name
    const el = this.findCustomFieldByName(name) || this.findStandardFieldByName(name);
    if (el) {
      const resolvedName = this.getFieldResolvedName(el);
      const errors = { ...this.errors };
      let changed = false;
      if (errors[resolvedName]) {
        delete errors[resolvedName];
        changed = true;
      }
      if (changed) {
        this.errors = errors;
      }
    } else if (this.errors[name]) {
      // fallback: if element not found, just remove by name
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [name]: _, ...restErrors } = this.errors;
      this.errors = restErrors;
    }
  }

  @Listen('clearFieldError')
  handleClearFieldError(event: CustomEvent<string>) {
    this.clearErrorForField(event.detail);
  }

  componentWillLoad() {
    this.formName = this.getUniqueFormName();
  }

  componentDidLoad() {
    this.formRef = this.hostEl.querySelector('form') || undefined;
    if (this.formRef && !this.disableFieldPrefix) {
      this.formRef.setAttribute('data-form-name', this.formName);
    }
    this.propagateConstantsToFields();
    this.updateFieldErrors();
  }

  componentDidUpdate() {
    this.propagateConstantsToFields();
    this.updateFieldErrors();
  }

  /**
   * Propagate constants to all form field components
   */
  private propagateConstantsToFields() {
    if (!this.formRef || !this.constants) return;

    const customElements = this.formRef.querySelectorAll('me-input, me-select, me-textarea, me-checkbox, me-radio, me-toggle');
    customElements.forEach((el: any) => {
      if ('constants' in el && !el.constants) {
        el.constants = this.constants;
      }
    });
  }

  render() {
    return (
      <form
        ref={el => (this.formRef = el)}
        action={this.action}
        method={this.method}
        data-form-name={this.disableFieldPrefix ? undefined : this.formName}
        onSubmit={this.handleSubmit}
        novalidate
      >
        {/* Form content slot */}
        <slot></slot>

        {/* Form actions slot */}
        <slot name="actions"></slot>

        {/* Only show submit/general error at the bottom of the form */}
        {this.errors['submit'] && (
          <div class="form-errors">
            <me-text variant="small" class="form-error-message">
              {this.errors['submit'].message}
            </me-text>
          </div>
        )}
      </form>
    );
  }
}
